import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { AI_ACCENT, AI_ACCENT_SOFT } from './aiTheme';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            fontSize: 13,
            lineHeight: 1.6,
            '& h1, & h2, & h3': {
                fontSize: 13.5,
                fontWeight: 700,
                margin: '12px 0 4px',
            },
            '& h1:first-child, & h2:first-child, & h3:first-child, & p:first-child': {
                marginTop: 0,
            },
            '& p': {
                margin: '0 0 8px',
            },
            '& ul': {
                margin: '0 0 8px',
                paddingLeft: 20,
            },
            '& li': {
                marginBottom: 2,
            },
            // Explicit request: the backend bolds named entities (people, organisations, courts/
            // insurers/employers, claim/document titles, dates, monetary amounts) - a pill reads
            // as "this is a tagged entity" at a glance, rather than just heavier text easy to skim
            // past. nowrap keeps a multi-word entity ("Peter Hanna") as one unbroken pill instead
            // of splitting across a line wrap.
            '& strong': {
                display: 'inline',
                fontWeight: 600,
                color: AI_ACCENT,
                backgroundColor: AI_ACCENT_SOFT,
                borderRadius: 999,
                padding: '1px 7px',
                whiteSpace: 'nowrap',
            },
        },
    })
);

// Renders inline emphasis (**bold**, *italic*/_italic_) as real React elements, never HTML - a
// single-pass split into text/strong/em tokens. Exported so a caller needing a single inline
// line (not a full block of headings/paragraphs/bullets) can render it directly inside its own
// Typography/span with its own font size, instead of wrapping it in MarkdownText's own root div
// (whose fixed fontSize/lineHeight would otherwise fight a smaller caller-chosen size).
export const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
    const tokens: React.ReactNode[] = [];
    const regex = /\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let index = 0;
    // tslint:disable-next-line: no-conditional-assignment
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            tokens.push(text.slice(lastIndex, match.index));
        }
        if (match[1] !== undefined) {
            tokens.push(<strong key={`${keyPrefix}-${index}`}>{match[1]}</strong>);
        } else {
            tokens.push(<em key={`${keyPrefix}-${index}`}>{match[2] ?? match[3]}</em>);
        }
        lastIndex = regex.lastIndex;
        index++;
    }
    if (lastIndex < text.length) {
        tokens.push(text.slice(lastIndex));
    }
    return tokens;
};

interface MarkdownTextProps {
    text: string;
    className?: string;
}

// A deliberately constrained Markdown-subset renderer (headings, paragraphs, bullets, bold/
// italic) - the backend's own documentAIResults.analysisText is "constrained customer-facing
// Markdown", with the explicit instruction to "render it with raw HTML disabled and sanitise the
// rendered output". Rather than pulling in a Markdown library plus an HTML sanitiser, this parses
// straight to React elements and never touches dangerouslySetInnerHTML, so there's no HTML
// injection surface to sanitise in the first place - React escapes ordinary text content itself.
export const MarkdownText: React.FC<MarkdownTextProps> = ({ text, className }) => {
    const classes = useStyles();
    const lines = text.split(/\r?\n/);

    const blocks: React.ReactNode[] = [];
    let bulletBuffer: string[] = [];
    let blockIndex = 0;

    const flushBullets = () => {
        if (bulletBuffer.length === 0) {
            return;
        }
        const items = bulletBuffer;
        blocks.push(
            <ul key={`ul-${blockIndex++}`}>
                {items.map((item, i) => (
                    <li key={i}>{renderInline(item, `li-${blockIndex}-${i}`)}</li>
                ))}
            </ul>
        );
        bulletBuffer = [];
    };

    lines.forEach(rawLine => {
        const line = rawLine.trimEnd();
        const headingMatch = /^(#{1,3})\s+(.*)$/.exec(line);
        const bulletMatch = /^[-*]\s+(.*)$/.exec(line);

        if (headingMatch) {
            flushBullets();
            const level = headingMatch[1].length;
            const HeadingTag = (level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3') as 'h1' | 'h2' | 'h3';
            blocks.push(<HeadingTag key={`h-${blockIndex++}`}>{renderInline(headingMatch[2], `h-${blockIndex}`)}</HeadingTag>);
            return;
        }

        if (bulletMatch) {
            bulletBuffer.push(bulletMatch[1]);
            return;
        }

        flushBullets();

        if (line.trim().length === 0) {
            return; // blank line - just a paragraph separator, no empty <p>
        }

        blocks.push(<p key={`p-${blockIndex++}`}>{renderInline(line, `p-${blockIndex}`)}</p>);
    });
    flushBullets();

    return <div className={`${classes.root} ${className || ''}`}>{blocks}</div>;
};
